import React, { FC, useCallback, useMemo, useRef, useState } from 'react';

import {
  StyledMapFeatureSvg,
  StyledUsaMapChartTooltip,
  StyledUsaMapChartWrapper
} from './UsaMapChart.styles';
import { AlbersUsa } from '@visx/geo';
import * as topojson from 'topojson-client';
import statesAbbr from './usa-abbreviations.json';
import topology from './usa-topology.json';
import usaCounties from './usa-counties.json';
import { localPoint } from '@visx/event';
import ChartTooltip from '../../components/ChartTooltip';
import { useTooltip } from '@visx/tooltip';
import { TooltipData } from '../../components/ChartTooltip/ChartTooltip';
import {
  FormatUtils,
  MAP_CHART_FIELDS_LABEL,
  METRICS,
  getMapColors,
  useWindowSize,
  getPopulationPerc,
  isReach
} from '../../utils';
import { Group } from '@visx/group';
import { LinearGradient } from '@visx/gradient';
import { Text } from '@visx/text';
import { Circle } from '@visx/shape';
import { scaleLinear, scaleSqrt } from '@visx/scale';
import uniqid from 'uniqid';
import { BoxV2, EmptyStateV2 } from '@cubeyou-team/hx-react-components';

type UsaMapChartProps = {
  type: string;
  data: any[];
  metric: string;
  showMoreDetail: boolean;
  onItemClick: (item: any) => void;
  color: string;
  emptyDataMessage: string;
};

let tooltipTimeout: number;

interface FeatureShape {
  type: 'Feature';
  id: string;
  geometry: { coordinates: [number, number][][]; type: 'Polygon' };
  properties: { name: string; iso_3166_2?: string };
}

const { features: states } = topojson.feature(
  topology,
  topology.objects.states
) as {
  type: 'FeatureCollection';
  features: FeatureShape[];
};

const UsaMapChart: FC<UsaMapChartProps> = ({
  type = 'states',
  data,
  metric,
  showMoreDetail,
  onItemClick,
  color,
  emptyDataMessage
}) => {
  const svgRef = useRef(null);
  const chartId = useRef(uniqid());
  const isEmpty = !data || data.length === 0;
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const yMax = height;
  const {
    tooltipOpen,
    tooltipLeft,
    tooltipTop,
    tooltipData,
    hideTooltip,
    showTooltip
  } = useTooltip<TooltipData>();

  const windowSize = useWindowSize();

  const chartRef = useCallback(
    (node) => {
      if (node != null) {
        setWidth(node.getBoundingClientRect().width);
        setHeight(node.getBoundingClientRect().height);
      }
    },
    [windowSize]
  );

  const counties = Object.keys(usaCounties).map((county) => {
    return {
      id: county,
      abbr: county.substring(county.length - 2, county.length).toUpperCase(),
      label: usaCounties[county].name,
      lat: usaCounties[county].value.lat,
      lng: usaCounties[county].value.lng
    };
  });

  const handleMouseMove = useCallback(
    (event: React.MouseEvent, feature: FeatureShape, color = '#000000') => {
      if (tooltipTimeout) clearTimeout(tooltipTimeout);
      if (!svgRef.current) return;
      const point = localPoint(svgRef.current, event);
      showTooltip({
        tooltipData: {
          ...feature,
          color: color
        },
        tooltipTop: point?.y,
        tooltipLeft: point?.x
      });
    },
    [svgRef, showTooltip]
  );

  const handleMouseLeave = useCallback(
    (event) => {
      tooltipTimeout = window.setTimeout(() => {
        hideTooltip();
      }, 0);
    },
    [hideTooltip]
  );

  const tooltipContent = useMemo(() => {
    if (showMoreDetail) {
      return (
        <React.Fragment>
          {tooltipOpen && tooltipData && (
            <ChartTooltip
              opened={tooltipOpen}
              top={tooltipTop || 0}
              left={tooltipLeft || 0}
              data={tooltipData}
            >
              {isReach(metric) && (
                <div>
                  <span>Reach: </span>
                  {FormatUtils.formattedValue(
                    getPopulationPerc(tooltipData),
                    METRICS.REACH
                  )}
                </div>
              )}
              {metric === METRICS.AFFINITY && (
                <div>
                  <span>Affinity: </span>
                  {FormatUtils.formattedValue(
                    tooltipData.target_audience_affinity,
                    METRICS.AFFINITY
                  )}
                </div>
              )}
              {metric === METRICS.BASE && (
                <div>
                  <span>Base: </span>
                  {FormatUtils.formattedValue(
                    tooltipData.base_population_perc,
                    METRICS.BASE
                  )}
                </div>
              )}
              {metric === METRICS.PENETRATION && (
                <div>
                  <span>Penetration: </span>
                  {FormatUtils.formattedValue(
                    tooltipData.penetration,
                    METRICS.PENETRATION
                  )}
                </div>
              )}
              {metric === METRICS.RELEVANCY && (
                <div>
                  <span>Relevance: </span>
                  {FormatUtils.formattedValue(
                    tooltipData.relevancy,
                    METRICS.RELEVANCY
                  )}
                </div>
              )}
            </ChartTooltip>
          )}
        </React.Fragment>
      );
    }

    return (
      <React.Fragment>
        {tooltipOpen && tooltipData && (
          <StyledUsaMapChartTooltip top={tooltipTop} left={tooltipLeft}>
            <div>{tooltipData && tooltipData.label}</div>
          </StyledUsaMapChartTooltip>
        )}
      </React.Fragment>
    );
  }, [
    showMoreDetail,
    tooltipLeft,
    tooltipTop,
    tooltipData,
    metric,
    tooltipOpen
  ]);

  const scale = (width + height) / 2;
  const centerX = width / 2;
  const centerY = height / 2;

  const max = (data && Math.max(...data.map((item) => item[metric]))) || 0;
  const min = (data && Math.min(...data.map((item) => item[metric]))) || 0;
  const colorsRange = getMapColors(color);
  const colorScale = useMemo(
    () =>
      scaleLinear({
        domain: [
          metric === METRICS.AFFINITY ? min : min * 100,
          metric === METRICS.AFFINITY ? max : max * 100
        ],
        range: colorsRange
      }),
    [metric, min, max, color]
  );

  const getFillColor = useCallback(
    (item) => {
      if (!metric) {
        return '#d8d7ea';
      }

      let value = item[metric] || 0;
      if (metric !== METRICS.AFFINITY) {
        value = value * 100;
      }
      return colorScale(value);
    },
    [metric, colorScale]
  );

  const rScale = useMemo(
    () =>
      scaleSqrt<number>({
        domain: [min, max],
        range: [0, 20],
        nice: true
      }),
    [min, max]
  );

  const getCircles = useCallback(
    (abbr, projection) => {
      const stateCounties = counties.filter((county) => abbr === county.abbr);
      return stateCounties.map((county) => {
        const countyData = data && data.find((item) => item.id === county.id);
        return {
          id: county.id,
          radius: (metric && countyData && rScale(countyData[metric])) || 0,
          position: projection([county.lng, county.lat]),
          data: countyData
        };
      });
    },
    [data, metric, rScale]
  );

  if (isEmpty) {
    return (
      <StyledUsaMapChartWrapper>
        <BoxV2 minWidth='100%'>
          <EmptyStateV2
            icon='analytics'
            mainText={emptyDataMessage}
            size='large'
          />
        </BoxV2>
      </StyledUsaMapChartWrapper>
    );
  }

  return (
    <StyledUsaMapChartWrapper ref={chartRef}>
      <svg width={width} height={height} ref={svgRef}>
        <AlbersUsa
          data={states}
          scale={scale}
          translate={[centerX, centerY - 25]}
        >
          {({ features }) =>
            features.map(({ feature, path, projection }, i) => {
              const abbr = statesAbbr.find((abbr) => abbr.id === feature.id);
              const abbrLabel = (abbr && abbr.label) || '';
              const included =
                data && data.find((item: any) => item.id === abbrLabel);

              if (!included || type === 'counties') {
                return (
                  <React.Fragment
                    key={`map-${chartId.current}-feature-fragment-${i}`}
                  >
                    <StyledMapFeatureSvg
                      key={`map-${chartId.current}-feature-svg-${i}`}
                      selectable={false}
                    >
                      <path
                        key={`map-${chartId.current}-feature-${i}`}
                        d={path || ''}
                        fill={type === 'counties' ? '#e6ebee' : '#d8d7ea'}
                        stroke={type === 'counties' ? '#afb3b4' : '#ffffff'}
                        strokeWidth={1}
                      />
                    </StyledMapFeatureSvg>
                  </React.Fragment>
                );
              }
              return (
                <React.Fragment
                  key={`map-${chartId.current}-feature-fragment-${i}`}
                >
                  <StyledMapFeatureSvg
                    key={`map-${chartId.current}-feature-svg-${i}`}
                    metric={metric}
                    selectable
                  >
                    <path
                      key={`map-${chartId.current}-feature-${i}`}
                      d={path || ''}
                      fill={getFillColor(included)}
                      stroke='#ffffff'
                      strokeWidth={1}
                      onMouseOver={(event) =>
                        handleMouseMove(event, included, getFillColor(included))
                      }
                      onMouseMove={(event) =>
                        handleMouseMove(event, included, getFillColor(included))
                      }
                      onMouseLeave={(event) => handleMouseLeave(event)}
                      onClick={() => {
                        onItemClick && onItemClick(included);
                      }}
                    />
                  </StyledMapFeatureSvg>
                </React.Fragment>
              );
            })
          }
        </AlbersUsa>
        {metric && type === 'counties' && (
          <AlbersUsa
            data={states}
            scale={scale}
            translate={[centerX, centerY - 25]}
          >
            {({ features }) =>
              features.map(({ feature, path, projection }, i) => {
                const abbr = statesAbbr.find((abbr) => abbr.id === feature.id);
                const abbrLabel = (abbr && abbr.label) || '';
                const circles = getCircles(abbrLabel, projection);

                return (
                  <React.Fragment
                    key={`map-${chartId.current}-feature-fragment-with-circle-${i}`}
                  >
                    <StyledMapFeatureSvg
                      key={`map-${chartId.current}-feature-svg-with-circle-${i}`}
                      selectable={false}
                    >
                      {circles &&
                        circles.map((circle, indx) => (
                          <Circle
                            key={`county-${chartId.current}-feature-${i}-${indx}`}
                            r={circle.radius}
                            cx={(circle.position && circle.position[0]) || 0}
                            cy={(circle.position && circle.position[1]) || 0}
                            fill={color || '#007ADD'}
                            fillOpacity={0.5}
                            onMouseOver={(event) =>
                              handleMouseMove(event, circle.data)
                            }
                            onMouseMove={(event) =>
                              handleMouseMove(event, circle.data)
                            }
                            onMouseLeave={(event) => handleMouseLeave(event)}
                          />
                        ))}
                    </StyledMapFeatureSvg>
                  </React.Fragment>
                );
              })
            }
          </AlbersUsa>
        )}
        {metric && type !== 'counties' && (
          <Group top={yMax - 40} left={centerX - 100}>
            <LinearGradient
              from={colorsRange[0]}
              to={colorsRange[1]}
              id={`legend-gradient-${color}`}
              vertical={false}
            />
            <Text
              fontSize='10px'
              fontWeight='bold'
              textAnchor='middle'
              verticalAnchor='middle'
              fill='#000000'
              width={100}
              height={20}
              x={0}
              y={-10}
            >
              {FormatUtils.formattedValue(min, metric)}
            </Text>
            <Text
              fontSize='10px'
              fontWeight='bold'
              textAnchor='middle'
              verticalAnchor='middle'
              fill='#000000'
              width={100}
              height={20}
              x={200}
              y={-10}
            >
              {`${FormatUtils.formattedValue(max, metric)}`}
            </Text>
            <rect
              width={200}
              height={20}
              fill={`url(#legend-gradient-${color})`}
            />
            <Text
              fontSize='10px'
              fontWeight='bold'
              textAnchor='middle'
              verticalAnchor='middle'
              fill='#000000'
              width={100}
              height={20}
              x={100}
              y={30}
            >
              {MAP_CHART_FIELDS_LABEL[metric]}
            </Text>
          </Group>
        )}
      </svg>
      {tooltipContent}
    </StyledUsaMapChartWrapper>
  );
};

export default UsaMapChart;
